/**
 * cart-gift-picker.js
 * Owns the shared gift-picker modal (snippets/cart-gift-picker-modal.liquid):
 * open/close, focus trap, and keeping its content in sync with the live cart.
 *
 * Does NOT contain any promo logic itself — the coffret/litre selection,
 * stepper, and reconciler behavior all still live entirely in
 * cart-bogo-routines.js / cart-bogo-litres.js, unchanged. This script only
 * moves already-rendered markup into view and manages the dialog chrome.
 *
 * Content sync: [data-gift-sections] (rendered hidden inside
 * [data-cart-drawer-body] by cart-drawer.liquid, kept fresh by every Section
 * Rendering API refresh) is copied via innerHTML string assignment into
 * [data-gift-modal-body] (in the static modal, outside the refreshed
 * region) on load and on every `cart:updated`. A plain string copy, not a
 * DOM move: an already-connected custom element that gets reparented via
 * appendChild fires disconnectedCallback then connectedCallback again on
 * the SAME node, which would re-run <cart-bogo-routines>'s/
 * <cart-bogo-litres>'s connectedCallback listener-binding a second time on
 * the same buttons -- a real double-binding bug. Assigning innerHTML instead
 * parses a fresh set of nodes into the modal each time, each getting exactly
 * one clean connectedCallback firing, while the hidden source copy inside
 * the drawer body (itself also freshly parsed by the drawer's own refresh)
 * is invisible and never interacted with.
 */
(function () {
  'use strict';

  var lastFocused = null;

  function syncContent() {
    var source = document.querySelector('[data-gift-sections]');
    var modalBody = document.querySelector('[data-gift-modal-body]');
    if (!source || !modalBody) return;
    modalBody.innerHTML = source.innerHTML;
  }

  function getFocusable(panel) {
    return panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function handleKeydown(e) {
    var overlay = document.querySelector('[data-gift-modal-overlay]');
    if (!overlay || !overlay.classList.contains('is-open')) return;

    if (e.key === 'Escape') {
      closeModal();
      return;
    }

    if (e.key !== 'Tab') return;
    var panel = document.querySelector('[data-gift-modal-panel]');
    if (!panel) return;
    var focusable = getFocusable(panel);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function openModal() {
    var overlay = document.querySelector('[data-gift-modal-overlay]');
    if (!overlay) return;
    syncContent();
    lastFocused = document.activeElement;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    var closeBtn = overlay.querySelector('[data-gift-modal-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    var overlay = document.querySelector('[data-gift-modal-overlay]');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-gift-picker-open]')) {
      openModal();
      return;
    }
    if (e.target.closest('[data-gift-modal-close]')) {
      closeModal();
      return;
    }
    if (e.target.matches('[data-gift-modal-overlay]')) {
      closeModal();
    }
  });

  document.addEventListener('keydown', handleKeydown);

  /* ── Auto-open on every add / quantity increase of a TRIGGER-eligible ────
   * product ─────────────────────────────────────────────────────────────
   * A gift is auto-picked by default (the reconciler's default-fill, see
   * cart-bogo-routines.js / cart-bogo-litres.js) with no click required, so
   * a customer can easily miss that it's actually changeable. Explicit
   * merchant request: pop the gift picker open every time a product is
   * added to cart or a line's quantity is increased (not just the first
   * time per session) whenever a routines/litres section is currently
   * showing, so the choice is never missed no matter how many times the
   * customer touches their cart.
   *
   * Tracks the cart's TRIGGER-ELIGIBLE quantity — the sum of quantities
   * across lines whose variant is in a currently-enabled promo's
   * triggerVariantIds (read from the [data-bogo-routines-config]/
   * [data-bogo-litres-config] JSON blocks cart-drawer.liquid renders
   * whenever that promo is effectively enabled) — not the cart's total
   * item_count. A real, confirmed report: adding an unrelated product that
   * has nothing to do with either promo (e.g. a plain shampoo, while a
   * coffret/litre promo is already active from an earlier purchase) still
   * increased cart.item_count and popped the picker open, even though
   * nothing about the customer's actual entitlement changed. Comparing
   * trigger-eligible quantity instead of raw item_count fixes this: only a
   * genuine coffret/litre purchase (or quantity bump) can increase it.
   *
   * Also excludes gift-flagged lines (_free_gift/_tier_gift/
   * _bogo_routines_gift/_bogo_litres_gift) from the sum — trigger and
   * display collections are commonly configured to be the SAME collection
   * (see .claude/rules/cart-promotions.md), so a picked gift's own variant
   * can itself be trigger-eligible; without this exclusion, picking a gift
   * would silently inflate the very quantity meant to represent "new
   * genuine purchases", matching the theme-wide "gift lines never earn
   * more gifts" rule.
   *
   * Deliberately still scoped to `cart:updated` only, never a plain
   * page-load sync: a passive page navigation that happens to land on a
   * page with an already-qualifying cart must not itself pop open the
   * cart drawer + modal unprompted. `cart:updated` firing already implies
   * the customer just took an action, and the drawer is typically already
   * opening as part of that same flow (see cart.md's `cart:add` → open
   * sequence), so this rides along with a UI change already in motion
   * rather than initiating a surprise one of its own.
   */
  var lastKnownTriggerQty = null; // null until seeded once from the real cart

  /**
   * Reads the trigger variant id set for whichever BOGO promos are
   * currently enabled. Both config blocks live in cart-drawer.liquid
   * outside the three refresh-replaced regions, so they're always present
   * in the DOM whenever their promo is on and never duplicated/lost on
   * refresh.
   * @returns {Set<number>}
   */
  function getTriggerVariantIds() {
    var ids = new Set();
    ['[data-bogo-routines-config]', '[data-bogo-litres-config]'].forEach(function (selector) {
      var el = document.querySelector(selector);
      if (!el) return;
      try {
        var config = JSON.parse(el.textContent);
        (config.triggerVariantIds || []).forEach(function (id) {
          ids.add(Number(id));
        });
      } catch (e) {
        /* Malformed/missing config — this promo just contributes no ids. */
      }
    });
    return ids;
  }

  /**
   * @param {object} item - a /cart.js line item
   * @returns {boolean}
   */
  function isGiftLine(item) {
    var props = item.properties || {};
    return (
      props['_free_gift'] === 'true' ||
      props['_tier_gift'] === 'true' ||
      props['_bogo_routines_gift'] === 'true' ||
      props['_bogo_litres_gift'] === 'true'
    );
  }

  /**
   * Refreshes lastKnownTriggerQty from the live cart and reports whether
   * trigger-eligible quantity is now higher than what was last recorded.
   * A no-op "false" until the first successful fetch has run (covers both
   * the initial page-load seed and any transient fetch failure) — there is
   * deliberately no separate seed step: the very first call this session
   * establishes the baseline without ever reporting an increase, since
   * `lastKnownTriggerQty === null` short-circuits before any comparison.
   * @returns {Promise<boolean>}
   */
  function refreshAndCheckIncrease() {
    return fetch('/cart.js')
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart) return false;
        var triggerIds = getTriggerVariantIds();
        var triggerQty = 0;
        (cart.items || []).forEach(function (item) {
          if (isGiftLine(item)) return;
          if (triggerIds.has(Number(item.variant_id))) {
            triggerQty += item.quantity;
          }
        });
        var wasSeeded = lastKnownTriggerQty !== null;
        var increased = wasSeeded && triggerQty > lastKnownTriggerQty;
        lastKnownTriggerQty = triggerQty;
        return increased;
      })
      .catch(function () {
        /* Network hiccup — treat as "no increase" for this pass; the next
           cart:updated retries with a fresh fetch. Leaves lastKnownTriggerQty
           untouched (still whatever it was, possibly still null), so a
           still-unseeded baseline keeps skipping rather than risk a false
           positive against no real data. */
        return false;
      });
  }

  function maybeAutoOpen() {
    var sections = document.querySelectorAll('[data-gift-sections] [data-gift-section]');
    if (!sections.length) return;

    var drawer = document.querySelector('[data-cart-drawer]');
    var drawerIsOpen = drawer && drawer.getAttribute('aria-hidden') === 'false';
    if (!drawerIsOpen && window.cartDrawer && typeof window.cartDrawer.open === 'function') {
      window.cartDrawer.open();
    }
    openModal();
  }

  /* Serializes every refreshAndCheckIncrease() call strictly one after
   * another rather than letting concurrent cart:updated events (this
   * theme's own action plus a same-tick reconciler reaction) fire off
   * overlapping /cart.js fetches whose responses could resolve out of
   * order and clobber lastKnownTriggerQty with a stale value. A single
   * aggregate counter makes this far less likely to cause a visible bug
   * than the old per-line tracking did, but chaining every check through
   * one promise costs nothing and removes the ordering question entirely —
   * each check's fetch only starts once the previous one has fully
   * resolved and written its result. */
  var checkQueue = Promise.resolve();

  /* Keep the modal's copy fresh even while it's open (or about to reopen)
     so a stepper click made just before another cart change doesn't leave
     the modal showing stale quantities. */
  document.addEventListener('cart:updated', function () {
    syncContent();
    checkQueue = checkQueue
      .then(function () {
        return refreshAndCheckIncrease();
      })
      .then(function (increased) {
        if (increased) maybeAutoOpen();
      });
  });

  /* Initial sync so the CTA + modal content matches page-load cart state
     even before any cart:updated fires (the drawer's own markup is
     server-rendered on every page load, not only via AJAX), plus seeding
     lastKnownTriggerQty for the increase check above via the same
     refreshAndCheckIncrease() used everywhere else — its own null-baseline
     guard means this first call can never itself report an increase, so
     it's safe to fire-and-forget without checking its result. Deliberately
     does NOT call maybeAutoOpen(): a passive page load landing on an
     already-qualifying cart must not itself pop the drawer + modal open
     unprompted, only a genuine cart:updated action should. */
  syncContent();
  checkQueue = checkQueue.then(function () {
    return refreshAndCheckIncrease();
  });
})();
